import React, { Fragment, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import StaffAllAttendance from "./StaffAllAttendance";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import OfficeGoogleMapPicker from "./OfficeGoogleMapPicker";

const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const weekDays = [
  { value: 0, label: "Sunday", short: "Sun" },
  { value: 1, label: "Monday", short: "Mon" },
  { value: 2, label: "Tuesday", short: "Tue" },
  { value: 3, label: "Wednesday", short: "Wed" },
  { value: 4, label: "Thursday", short: "Thu" },
  { value: 5, label: "Friday", short: "Fri" },
  { value: 6, label: "Saturday", short: "Sat" },
];

const statuses = ["", "P", "HD", "A", "L", "WO", "H"];

const statusLabels = {
  P: "Present",
  HD: "Half Day",
  A: "Absent",
  L: "Paid Leave",
  WO: "Weekly Off",
  H: "Holiday",
};

const gridStatusVariant = {
  P: "success",
  HD: "warning",
  A: "danger",
  L: "info",
  WO: "secondary",
  H: "primary",
};

const statusVariant = {
  PRESENT: "success",
  HALF_DAY: "warning",
  ABSENT: "danger",
  LEAVE: "info",
  WEEKLY_OFF: "secondary",
  HOLIDAY: "primary",
  MANUAL: "dark",
};

const blankConfig = {
  officeStartTime: "09:30",
  officeEndTime: "18:30",
  graceMinutes: 15,
  fullDayMinutes: 480,
  halfDayMinutes: 240,
  weekendDays: [0],
  salaryCalculationBasis: "CALENDAR_DAYS",
  halfDayPayRatio: 0.5,
  paidLeave: true,
  paidWeekend: true,
  paidHoliday: true,
  autoHalfDayFromPunchHours: true,
  autoAbsentForShortHours: false,
  officeLocations: [],
  officeLocation: { latitude: null, longitude: null, address: "" },
  allowedRadiusMeters: 0,
};

const formatTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatLocation = (location) => {
  if (!location?.latitude || !location?.longitude) return "-";
  if (location.address) return location.address;
  return `${Number(location.latitude).toFixed(5)}, ${Number(location.longitude).toFixed(5)}`;
};

const getDefaultLogDay = (targetMonth = new Date().getMonth(), targetYear = new Date().getFullYear()) => {
  const today = new Date();
  return Number(targetMonth) === today.getMonth() && Number(targetYear) === today.getFullYear()
    ? String(today.getDate())
    : "";
};

const formatDuration = (minutes) => {
  const total = Number(minutes || 0);
  if (!total) return "-";
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${hours}h ${mins}m`;
};

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const getEmployeeId = (record) => record.employeeId?._id || record.employeeId;

// Inline map preview modal 
const MapPreviewModal = ({ show, onHide, record, onLiveTrack }) => {
  const hasIn = record?.locationIn?.latitude && record?.locationIn?.longitude;
  const hasOut = record?.locationOut?.latitude && record?.locationOut?.longitude;

  // Build Google Maps Embed URL
  // If both locations: show directions (in → out)
  // If only one: show single marker
  const buildEmbedUrl = () => {
    if (!MAPS_API_KEY) return "";
    if (hasIn && hasOut) {
      const origin = `${record.locationIn.latitude},${record.locationIn.longitude}`;
      const dest = `${record.locationOut.latitude},${record.locationOut.longitude}`;
      return `https://www.google.com/maps/embed/v1/directions?key=${MAPS_API_KEY}&origin=${origin}&destination=${dest}&mode=driving`;
    }
    if (hasIn) {
      const q = `${record.locationIn.latitude},${record.locationIn.longitude}`;
      return `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${q}&zoom=16`;
    }
    if (hasOut) {
      const q = `${record.locationOut.latitude},${record.locationOut.longitude}`;
      return `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${q}&zoom=16`;
    }
    return "";
  };

  const embedUrl = buildEmbedUrl();
  const dateLabel = record?.dateKey
    ? new Date(`${record.dateKey}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton style={{ padding: "12px 18px" }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          Location Map & Photos — {dateLabel}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <Row className="g-0">
          {/* Left Column: Punch In & Punch Out Details with Proper Photos */}
          <Col md={5} className="p-3 bg-light border-end d-flex flex-column gap-3" style={{ maxHeight: 460, overflowY: "auto" }}>
            {/* Punch In Card */}
            <div className="p-2 bg-white rounded border shadow-sm">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="badge bg-success-subtle text-success border border-success fw-bold px-2 py-1" style={{ fontSize: 11 }}>
                  ● PUNCH IN
                </span>
                <strong style={{ fontSize: 14 }}>{formatTime(record?.punchInAt)}</strong>
              </div>

              {record?.photoIn ? (
                <div className="mb-2 text-center">
                  <a href={getFileUrl(record.photoIn)} target="_blank" rel="noreferrer" title="Click to view full photo">
                    <img
                      src={getFileUrl(record.photoIn)}
                      alt="Punch In Selfie"
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      }}
                    />
                  </a>
                  <div className="text-muted mt-1" style={{ fontSize: 10 }}>Click image to expand</div>
                </div>
              ) : (
                <div className="text-center py-3 mb-2 rounded bg-light border text-muted small">
                  No In photo captured
                </div>
              )}

              <div className="small text-muted" style={{ fontSize: 11, lineHeight: 1.3 }}>
                <div className="fw-semibold text-dark mb-0.5">Coordinates:</div>
                {hasIn ? (
                  <div>{Number(record.locationIn.latitude).toFixed(5)}, {Number(record.locationIn.longitude).toFixed(5)}</div>
                ) : (
                  <div>No GPS coordinates</div>
                )}
                {record?.locationIn?.address && (
                  <div className="mt-1 text-truncate" title={record.locationIn.address}>
                    {record.locationIn.address}
                  </div>
                )}
              </div>
            </div>

            {/* Punch Out Card */}
            <div className="p-2 bg-white rounded border shadow-sm">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="badge bg-danger-subtle text-danger border border-danger fw-bold px-2 py-1" style={{ fontSize: 11 }}>
                  ● PUNCH OUT
                </span>
                <strong style={{ fontSize: 14 }}>{formatTime(record?.punchOutAt)}</strong>
              </div>

              {record?.photoOut ? (
                <div className="mb-2 text-center">
                  <a href={getFileUrl(record.photoOut)} target="_blank" rel="noreferrer" title="Click to view full photo">
                    <img
                      src={getFileUrl(record.photoOut)}
                      alt="Punch Out Selfie"
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      }}
                    />
                  </a>
                  <div className="text-muted mt-1" style={{ fontSize: 10 }}>Click image to expand</div>
                </div>
              ) : (
                <div className="text-center py-3 mb-2 rounded bg-light border text-muted small">
                  No Out photo captured
                </div>
              )}

              <div className="small text-muted" style={{ fontSize: 11, lineHeight: 1.3 }}>
                <div className="fw-semibold text-dark mb-0.5">Coordinates:</div>
                {hasOut ? (
                  <div>{Number(record.locationOut.latitude).toFixed(5)}, {Number(record.locationOut.longitude).toFixed(5)}</div>
                ) : (
                  <div>No GPS coordinates</div>
                )}
                {record?.locationOut?.address && (
                  <div className="mt-1 text-truncate" title={record.locationOut.address}>
                    {record.locationOut.address}
                  </div>
                )}
              </div>
            </div>
          </Col>

          {/* Right Column: Google Maps Iframe */}
          <Col md={7} className="d-flex flex-column">
            {embedUrl ? (
              <iframe
                title="Punch Location Map"
                src={embedUrl}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 460, display: "block" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="text-center text-muted d-flex align-items-center justify-content-center flex-grow-1" style={{ minHeight: 350 }}>
                No GPS map data available
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="py-2 d-flex justify-content-between">
        <div className="d-flex gap-2">
          {(hasIn || hasOut) ? (
            <a
              href={`https://www.google.com/maps?q=${(hasIn ? record.locationIn : record.locationOut).latitude},${(hasIn ? record.locationIn : record.locationOut).longitude}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-sm btn-outline-primary"
            >
              Open in Google Maps ↗
            </a>
          ) : null}
          {onLiveTrack && (
            <Button size="sm" variant="success" onClick={onLiveTrack}>
              Live GPS Route
            </Button>
          )}
        </div>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

const StaffAttendancePage = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [punches, setPunches] = useState([]);
  const [punchMap, setPunchMap] = useState({});
  const [config, setConfig] = useState(blankConfig);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [activeTab, setActiveTab] = useState("monthly"); // "monthly" | "logs"
  const [logDay, setLogDay] = useState(() => getDefaultLogDay());
  const [month, setMonth] = usePersistentState("staff_att_month", new Date().getMonth());
  const [year, setYear] = usePersistentState("staff_att_year", new Date().getFullYear());
  const [search, setSearch] = usePersistentState("staff_att_search", "");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [mapRecord, setMapRecord] = useState(null);
  const [assignOfficeModal, setAssignOfficeModal] = useState({
    show: false,
    staff: null,
    selectedOffices: [],
    selectedStaffIds: [],
    saving: false,
  });

  const openAssignOfficeModal = (staffItem) => {
    setAssignOfficeModal({
      show: true,
      staff: staffItem,
      selectedOffices: Array.isArray(staffItem.assignedOffices) ? [...staffItem.assignedOffices] : [],
      selectedStaffIds: [staffItem._id],
      saving: false,
    });
  };

  const openBulkAssignModal = () => {
    setAssignOfficeModal({
      show: true,
      staff: null,
      selectedOffices: [],
      selectedStaffIds: staff.map((s) => s._id),
      saving: false,
    });
  };

  const handleToggleOffice = (officeName) => {
    setAssignOfficeModal((prev) => {
      const exists = prev.selectedOffices.includes(officeName);
      const next = exists
        ? prev.selectedOffices.filter((n) => n !== officeName)
        : [...prev.selectedOffices, officeName];
      return { ...prev, selectedOffices: next };
    });
  };

  const handleSelectAllOffices = () => {
    setAssignOfficeModal((prev) => ({ ...prev, selectedOffices: [] }));
  };

  const handleSaveAssignedOffices = async () => {
    try {
      setAssignOfficeModal((prev) => ({ ...prev, saving: true }));
      const { staff: targetStaff, selectedStaffIds, selectedOffices } = assignOfficeModal;

      if (targetStaff) {
        await api.put(`/staff-attendance/employees/${targetStaff._id}/assigned-offices`, {
          assignedOffices: selectedOffices,
        });
        swal("Success", `Office assignment updated for ${targetStaff.name}`, "success");
      } else {
        if (!selectedStaffIds.length) {
          swal("Warning", "Please select at least one staff member", "warning");
          setAssignOfficeModal((prev) => ({ ...prev, saving: false }));
          return;
        }
        await api.put("/staff-attendance/employees/bulk-assigned-offices", {
          employeeIds: selectedStaffIds,
          assignedOffices: selectedOffices,
        });
        swal("Success", `Office assignment updated for ${selectedStaffIds.length} staff members`, "success");
      }

      setAssignOfficeModal({ show: false, staff: null, selectedOffices: [], selectedStaffIds: [], saving: false });
      await loadStaffAndAttendance();
    } catch (err) {
      swal("Error", err.response?.data?.message || "Failed to update office assignment", "error");
      setAssignOfficeModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dayIndexes = useMemo(
    () => Array.from({ length: daysInMonth }, (_, index) => index),
    [daysInMonth],
  );

  const calendarCells = useMemo(() => {
    const firstWeekDay = new Date(year, month, 1).getDay();
    const cells = [];

    for (let index = 0; index < firstWeekDay; index += 1) {
      cells.push(null);
    }

    dayIndexes.forEach((day) => cells.push(day));

    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    return cells;
  }, [dayIndexes, month, year]);

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    loadStaffAndAttendance();
  }, [month, year]);

  const loadConfig = async () => {
    try {
      const res = await api.get("/staff-attendance/config");
      if (res.data.success) {
        const data = res.data.data || {};
        let officeLocations = Array.isArray(data.officeLocations) && data.officeLocations.length > 0
          ? data.officeLocations
          : [];
        if (!officeLocations.length && data.officeLocation?.latitude) {
          officeLocations = [{
            name: data.officeLocation.address || "Main Office",
            latitude: data.officeLocation.latitude,
            longitude: data.officeLocation.longitude,
            address: data.officeLocation.address || "",
            allowedRadiusMeters: Number(data.allowedRadiusMeters || 100),
          }];
        }
        setConfig({ ...blankConfig, ...data, officeLocations });
      }
    } catch (error) {
      swal("Error", "Failed to load attendance settings", "error");
    }
  };

  const loadStaffAndAttendance = async () => {
    try {
      setLoading(true);
      const [staffRes, attendanceRes, punchRes] = await Promise.all([
        api.get("/staff-salary/overview", { params: { month, year } }),
        api.get(`/staff-attendance/monthly/${month}/${year}`),
        api.get("/staff-attendance/punches", { params: { month, year } }),
      ]);

      const staffList = staffRes.data.data || [];
      const records = attendanceRes.data.data || [];
      const punchRecords = punchRes.data.data || [];
      const recordMap = new Map(records.map((record) => [record.employeeId, record]));
      const nextPunchMap = {};

      punchRecords.forEach((record) => {
        const employeeId = getEmployeeId(record);
        if (!employeeId || !record.dateKey) return;
        const day = Number(record.dateKey.split("-")[2]) - 1;
        nextPunchMap[`${employeeId}-${day}`] = record;
      });

      const grid = {};
      staffList.forEach((item) => {
        const record = recordMap.get(item._id);
        grid[item._id] = record?.days?.length
          ? record.days.slice(0, daysInMonth)
          : Array(daysInMonth).fill("");

        while (grid[item._id].length < daysInMonth) grid[item._id].push("");
      });

      setStaff(staffList);
      setAttendance(grid);
      setPunches(punchRecords);
      setPunchMap(nextPunchMap);
      setSelectedStaff((current) =>
        current ? staffList.find((item) => item._id === current._id) || null : null,
      );
    } catch (error) {
      swal("Error", "Failed to load staff attendance", "error");
    } finally {
      setLoading(false);
    }
  };

  const unenrollStaff = async (item) => {
    try {
      const willUnenroll = await swal({
        title: `Unenroll ${item.name}?`,
        text: `Are you sure you want to remove "${item.name}" from Payroll Attendance & Salary roster?\n\nThey can be re-enrolled anytime from Staff Salary Overview.`,
        icon: "warning",
        buttons: ["Cancel", "Yes, Unenroll"],
        dangerMode: true,
      });

      if (!willUnenroll) return;

      setLoading(true);
      const res = await api.post("/employees/payroll-staff/unenroll", {
        employeeId: item._id,
        userId: item.userId?._id || item.userId,
      });

      if (res.data?.success) {
        swal("Unenrolled!", `"${item.name}" has been removed from payroll roster.`, "success");
        await loadStaffAndAttendance();
      } else {
        swal("Error", res.data?.message || "Failed to unenroll staff", "error");
      }
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to unenroll staff", "error");
    } finally {
      setLoading(false);
    }
  };

  const isWeekendIndex = (dayIndex) =>
    (config.weekendDays || []).map(Number).includes(new Date(year, month, dayIndex + 1).getDay());

  const applyWeekendStatus = (days) =>
    days.map((status, index) => (isWeekendIndex(index) ? "WO" : status));

  const cycleStatus = (employeeId, dayIndex) => {
    if (isWeekendIndex(dayIndex)) return;
    setAttendance((prev) => {
      const days = [...(prev[employeeId] || Array(daysInMonth).fill(""))];
      const currentIndex = statuses.indexOf(days[dayIndex]);
      days[dayIndex] = statuses[(currentIndex + 1) % statuses.length];
      return { ...prev, [employeeId]: applyWeekendStatus(days) };
    });
  };

  const saveSelectedAttendance = async () => {
    if (!selectedStaff) return;

    try {
      setSaving(true);
      await api.post("/staff-attendance/monthly/save", {
        employeeId: selectedStaff._id,
        month,
        year,
        days: attendance[selectedStaff._id] || Array(daysInMonth).fill(""),
      });
      swal("Saved", "Staff attendance saved successfully", "success");
      loadStaffAndAttendance();
    } catch (error) {
      swal("Error", "Failed to save staff attendance", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSavingConfig(true);
      const res = await api.put("/staff-attendance/config", config);
      if (res.data.success) {
        const data = res.data.data || {};
        const officeLocations = Array.isArray(data.officeLocations) ? data.officeLocations : [];
        setConfig({ ...blankConfig, ...data, officeLocations });
      }
      swal("Saved", "Attendance settings saved successfully", "success");
      setShowConfig(false);
      loadStaffAndAttendance();
    } catch (error) {
      swal("Error", error.response?.data?.message || "Failed to save attendance settings", "error");
    } finally {
      setSavingConfig(false);
    }
  };

  const toggleWeekend = (day) => {
    const current = new Set((config.weekendDays || []).map(Number));
    if (current.has(day)) current.delete(day);
    else current.add(day);
    setConfig({ ...config, weekendDays: [...current].sort() });
  };

  const addOfficeLocation = () => {
    const list = config.officeLocations || [];
    const newOffice = {
      name: `Office ${list.length + 1}`,
      latitude: "",
      longitude: "",
      address: "",
      allowedRadiusMeters: 100,
    };
    setConfig((current) => ({
      ...current,
      officeLocations: [...(current.officeLocations || []), newOffice],
    }));
  };

  const updateOfficeItem = (index, field, value) => {
    setConfig((current) => {
      const list = [...(current.officeLocations || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...current, officeLocations: list };
    });
  };

  const removeOfficeItem = (index) => {
    setConfig((current) => {
      const list = [...(current.officeLocations || [])];
      list.splice(index, 1);
      return { ...current, officeLocations: list };
    });
  };

  const [activeMapIndex, setActiveMapIndex] = useState(null);
  const [locatingOfficeIndex, setLocatingOfficeIndex] = useState(null);

  const handleCaptureLocationForItem = (index) => {
    if (!navigator.geolocation) {
      swal("Error", "Location is not supported in this browser", "error");
      return;
    }

    setLocatingOfficeIndex(index);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const uLat = Number(position.coords.latitude).toFixed(7);
        const uLng = Number(position.coords.longitude).toFixed(7);
        updateOfficeItem(index, "latitude", uLat);
        updateOfficeItem(index, "longitude", uLng);

        // Try reverse geocode if address empty
        const office = (config.officeLocations || [])[index];
        if (!office?.address && window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat: Number(uLat), lng: Number(uLng) } }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              updateOfficeItem(index, "address", results[0].formatted_address);
            }
            setLocatingOfficeIndex(null);
          });
        } else {
          setLocatingOfficeIndex(null);
        }
      },
      () => {
        setLocatingOfficeIndex(null);
        swal("Error", "Allow location permission to set office location", "error");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
    );
  };

  const filteredStaff = staff.filter((item) => {
    const keyword = search.toLowerCase();
    if (!keyword) return true;
    return (
      item.name?.toLowerCase().includes(keyword) ||
      item.email?.toLowerCase().includes(keyword) ||
      item.mobile?.toLowerCase().includes(keyword)
    );
  });

  const countStatus = (employeeId, status) =>
    (attendance[employeeId] || []).filter((item) => item === status).length;

  const getPunch = (employeeId, dayIndex) => punchMap[`${employeeId}-${dayIndex}`];

  const selectedPunches = useMemo(() => {
    if (!selectedStaff) return [];

    const matchIds = new Set(
      [selectedStaff._id, selectedStaff.userId?._id, selectedStaff.userId]
        .filter(Boolean)
        .map((id) => String(id))
    );

    return punches
      .filter((record) => matchIds.has(String(getEmployeeId(record))) || matchIds.has(String(record.employeeId?.userId?._id || record.employeeId?.userId || "")))
      .filter((record) => !logDay || Number((record.dateKey || "").split("-")[2]) === Number(logDay))
      .sort((a, b) => (a.dateKey || "").localeCompare(b.dateKey || ""));
  }, [logDay, punches, selectedStaff]);

  const selectedTotalMinutes = selectedPunches.reduce(
    (sum, record) => sum + Number(record.totalMinutes || 0),
    0,
  );

  const renderSummaryBadge = (item, status, label) => (
    <Badge bg={gridStatusVariant[status]} className="me-1 mb-1">
      {label}: {countStatus(item._id, status)}
    </Badge>
  );

  return (
    <Fragment>
      <PageTitle activeMenu="Staff Attendance" motherMenu="HR" />

      {/* Switch between Monthly Summary & Daily Punch Register */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <ul className="nav nav-pills gap-1 p-1 bg-white border rounded shadow-sm">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link fw-semibold px-3 py-2 ${activeTab === "monthly" ? "active bg-primary text-white shadow-sm" : "text-secondary"}`}
              onClick={() => setActiveTab("monthly")}
              style={{ borderRadius: 6, border: "none", transition: "all 0.2s" }}
            >
              <i className="fas fa-calendar-alt me-2"></i> Monthly Staff Attendance
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link fw-semibold px-3 py-2 ${activeTab === "logs" ? "active bg-primary text-white shadow-sm" : "text-secondary"}`}
              onClick={() => setActiveTab("logs")}
              style={{ borderRadius: 6, border: "none", transition: "all 0.2s" }}
            >
              <i className="fas fa-list-alt me-2"></i> Daily Punch Logs & Register
            </button>
          </li>
        </ul>
      </div>

      {activeTab === "logs" ? (
        <StaffAllAttendance embedded={true} />
      ) : (
        <Card>
          <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <Card.Title>Staff Attendance</Card.Title>
              <div className="text-muted small">
                Office {config.officeStartTime} - {config.officeEndTime} | Full {config.fullDayMinutes} min | Half {config.halfDayMinutes} min
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="success"
                className="d-flex align-items-center gap-1 shadow-sm"
                onClick={() => navigate("/live-tracking")}
              >
                Live Tracking
              </Button>
              <Button variant="outline-primary" onClick={openBulkAssignModal}>
                <i className="fas fa-building me-1"></i> Assign Offices
              </Button>
              <Button variant="outline-primary" onClick={() => setShowConfig(true)}>
                Attendance Settings
              </Button>
            </div>
          </Card.Header>
        <Card.Body>
          <Row className="g-2 mb-3 align-items-end">
            <Col md={3}>
              <Form.Label>Month</Form.Label>
              <Form.Select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {months.map((item, index) => (
                  <option key={item} value={index}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Year</Form.Label>
              <Form.Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {[2024, 2025, 2026, 2027, 2028].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Search</Form.Label>
              <Form.Control
                placeholder="Search staff"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover className="align-middle">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Staff</th>
                    <th>Attendance Summary</th>
                    <th style={{ minWidth: 170 }}>Assigned Office</th>
                    <th>Salary</th>
                    <th>Total Hours</th>
                    <th className="text-end text-nowrap" style={{ minWidth: 120 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        No payroll staff enrolled
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((item) => {
                      const staffPunches = punches.filter((record) => getEmployeeId(record) === item._id);
                      const totalMinutes = staffPunches.reduce(
                        (sum, record) => sum + Number(record.totalMinutes || 0),
                        0,
                      );

                      return (
                        <tr key={item._id}>
                          <td>
                            <strong>{item.name}</strong>
                            <div className="text-muted small">{item.email || item.mobile || "Staff"}</div>
                          </td>
                          <td>
                            {renderSummaryBadge(item, "P", "P")}
                            {renderSummaryBadge(item, "HD", "HD")}
                            {renderSummaryBadge(item, "A", "A")}
                            {renderSummaryBadge(item, "L", "PL")}
                            {renderSummaryBadge(item, "WO", "WO")}
                            {renderSummaryBadge(item, "H", "H")}
                          </td>
                          <td>
                            {Array.isArray(item.assignedOffices) && item.assignedOffices.length > 0 ? (
                              <div className="d-flex flex-wrap gap-1 align-items-center">
                                {item.assignedOffices.map((off, oIdx) => (
                                  <Badge key={oIdx} bg="info" className="fw-normal" style={{ fontSize: "11px" }}>
                                    <i className="fas fa-map-marker-alt me-1"></i>
                                    {off}
                                  </Badge>
                                ))}
                                <Button
                                  variant="link"
                                  className="p-0 text-primary small ms-1 shadow-none"
                                  style={{ fontSize: 11 }}
                                  onClick={() => openAssignOfficeModal(item)}
                                  title="Change Assigned Office"
                                >
                                  <i className="fas fa-pencil-alt"></i>
                                </Button>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center gap-1">
                                <Badge bg="success" className="bg-opacity-10 text-success border border-success fw-normal" style={{ fontSize: "11px" }}>
                                  Any Location (Unrestricted)
                                </Badge>
                                <Button
                                  variant="link"
                                  className="p-0 text-primary small ms-1 shadow-none"
                                  style={{ fontSize: 11 }}
                                  onClick={() => openAssignOfficeModal(item)}
                                  title="Assign Specific Office"
                                >
                                  <i className="fas fa-pencil-alt"></i>
                                </Button>
                              </div>
                            )}
                          </td>
                          <td>
                            <strong>{formatMoney(item.payroll?.payableAmount)}</strong>
                            <div className="text-muted small">
                              Gross {formatMoney(item.gross)} | Deduction {formatMoney(item.payroll?.deductionAmount)}
                            </div>
                          </td>
                          <td>{formatDuration(totalMinutes)}</td>
                          <td className="text-end text-nowrap" style={{ minWidth: 120 }}>
                            <div className="d-flex gap-1 justify-content-end align-items-center flex-nowrap">
                              <Button size="sm" variant="primary" onClick={() => setSelectedStaff(item)}>
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="d-inline-flex align-items-center justify-content-center"
                                style={{ width: 32, height: 31 }}
                                title="Unenroll from Payroll"
                                onClick={() => unenrollStaff(item)}
                              >
                                <i className="fa fa-user-minus"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
      )}

      <Modal show={Boolean(selectedStaff)} onHide={() => setSelectedStaff(null)} size="xl" dialogClassName="staff-attendance-detail-modal">
        <Modal.Header closeButton>
          <div className="d-flex justify-content-between align-items-center w-100 me-2">
            <Modal.Title>{selectedStaff?.name || "Staff"} - {months[month]} {year}</Modal.Title>
            <Button
              size="sm"
              variant="success"
              className="d-flex align-items-center gap-1"
              onClick={() => {
                const uId = selectedStaff?.userId?._id || selectedStaff?.userId || selectedStaff?._id;
                navigate(`/live-tracking?staffId=${uId}`);
              }}
            >
              Live Track Staff
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "calc(100vh - 160px)", overflowY: "auto", overflowX: "hidden" }}>
          {selectedStaff && <Row className="g-3">
            <Col lg={8} className="d-flex flex-column" style={{ minHeight: 0 }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Attendance Calendar</h5>
                  <div className="d-flex flex-wrap gap-1">
                    {renderSummaryBadge(selectedStaff, "P", "P")}
                    {renderSummaryBadge(selectedStaff, "HD", "HD")}
                    {renderSummaryBadge(selectedStaff, "A", "A")}
                    {renderSummaryBadge(selectedStaff, "L", "PL")}
                    {renderSummaryBadge(selectedStaff, "WO", "WO")}
                    {renderSummaryBadge(selectedStaff, "H", "H")}
                  </div>
                </div>
                <div className="calendar-filter-controls">
                  <Form.Select size="sm" value={month} onChange={(e) => { const nextMonth = Number(e.target.value); setMonth(nextMonth); setLogDay(getDefaultLogDay(nextMonth, year)); }}>
                    {months.map((item, index) => <option key={item} value={index}>{item}</option>)}
                  </Form.Select>
                  <Form.Select size="sm" value={year} onChange={(e) => { const nextYear = Number(e.target.value); setYear(nextYear); setLogDay(getDefaultLogDay(month, nextYear)); }}>
                    {[2024, 2025, 2026, 2027, 2028].map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </div>
              </div>

              <div className="table-responsive flex-grow-1" style={{ overflowX: "auto" }}>
                <Table bordered className="text-center align-middle mb-0" style={{ tableLayout: "fixed", width: "100%" }}>
                  <thead><tr>{weekDays.map((day) => <th key={day.value}>{day.short}</th>)}</tr></thead>
                  <tbody>
                    {Array.from({ length: calendarCells.length / 7 }, (_, rowIndex) => (
                      <tr key={rowIndex}>
                        {calendarCells.slice(rowIndex * 7, rowIndex * 7 + 7).map((day, cellIndex) => {
                          const punch = day !== null ? getPunch(selectedStaff._id, day) : null;
                          const isWeekend = day !== null && isWeekendIndex(day);
                          const status = day !== null ? (isWeekend ? "WO" : attendance[selectedStaff._id]?.[day] || "") : "";
                          return (
                            <td key={`${rowIndex}-${cellIndex}`} style={{ width: "14.28%", height: 104, padding: 6, verticalAlign: "top" }}>
                              {day !== null && <div className="h-100 d-flex flex-column align-items-start text-start gap-1">
                                <div className="d-flex justify-content-between align-items-center w-100">
                                  <strong>{day + 1}</strong>
                                  <Button size="sm" disabled={isWeekend} variant={status ? gridStatusVariant[status] || "primary" : "outline-secondary"} onClick={() => cycleStatus(selectedStaff._id, day)} style={{ minWidth: 38, padding: "0.15rem 0.35rem", fontSize: 11 }}>{status || "-"}</Button>
                                </div>
                                <div className="small text-muted text-truncate w-100">{statusLabels[status] || "Not marked"}</div>
                                {punch && <div className="small text-muted w-100" style={{ lineHeight: 1.2, fontSize: 11 }}><div>In {formatTime(punch.punchInAt)}</div><div>Out {formatTime(punch.punchOutAt)}</div><div>{formatDuration(punch.totalMinutes)}</div></div>}
                              </div>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Col>
            <Col lg={4} className="staff-attendance-side">
              <div className="staff-summary-panel">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="mb-0">Salary Summary</h5>
                  <Badge bg="light" text="dark">{months[month]}</Badge>
                </div>

                <div className="salary-metric-grid">
                  <div className="salary-metric salary-metric-primary">
                    <span>Net Payable</span>
                    <strong>{formatMoney(selectedStaff.payroll?.totalPayableWithReimbursement || selectedStaff.payroll?.payableAmount)}</strong>
                    <small>Gross {formatMoney(selectedStaff.gross)}</small>
                  </div>
                  <div className="salary-metric">
                    <span>Reimbursement</span>
                    <strong className="text-success">{formatMoney(selectedStaff.payroll?.reimbursementAmount || 0)}</strong>
                    <small>{selectedStaff.payroll?.reimbursementCount || 0} claims approved</small>
                  </div>
                  <div className="salary-metric">
                    <span>Deduction</span>
                    <strong>{formatMoney(selectedStaff.payroll?.deductionAmount)}</strong>
                    <small>Per day {formatMoney(selectedStaff.payroll?.perDaySalary)}</small>
                  </div>
                  <div className="salary-metric">
                    <span>Paid Days</span>
                    <strong>{selectedStaff.payroll?.paidDays || 0}</strong>
                    <small>Salary days {selectedStaff.payroll?.salaryDays || daysInMonth}</small>
                  </div>
                  <div className="salary-metric">
                    <span>Punch Hours</span>
                    <strong>{formatDuration(selectedTotalMinutes)}</strong>
                    <small>{selectedPunches.length} records</small>
                  </div>
                </div>

                {/* Salary Structure */}
                {selectedStaff.salary && (
                  <>
                    <div className="detail-section-title mt-3 mb-2">Salary Structure</div>
                    <div className="detail-grid">
                      {[
                        { label: "Basic", value: selectedStaff.salary.basic },
                        { label: "DA", value: selectedStaff.salary.da },
                        { label: "HRA", value: selectedStaff.salary.hra },
                        { label: "Medical", value: selectedStaff.salary.medical },
                        { label: "Conveyance", value: selectedStaff.salary.conveyance },
                        { label: "Gross", value: selectedStaff.gross, highlight: true },
                      ].map(({ label, value, highlight }) => (
                        <div key={label} className={`detail-row ${highlight ? "detail-row-highlight" : ""}`}>
                          <span>{label}</span>
                          <strong>{formatMoney(value || 0)}</strong>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Compliance & Deductions */}
                {selectedStaff.compliance && (
                  <>
                    <div className="detail-section-title mt-3 mb-2">Compliance / Deductions</div>
                    <div className="detail-grid">
                      {selectedStaff.compliance.pfApplicable && (<>
                        <div className="detail-row">
                          <span>PF Employee ({selectedStaff.compliance.pfRate || 12}%)</span>
                          <strong>{formatMoney(selectedStaff.payroll?.pfEmployeeDeduction || 0)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>PF Employer ({selectedStaff.compliance.pfRate || 12}%)</span>
                          <strong className="text-muted">{formatMoney(selectedStaff.payroll?.pfEmployerDeduction || 0)}</strong>
                        </div>
                        {selectedStaff.compliance.uan && (
                          <div className="detail-row">
                            <span>UAN</span>
                            <strong>{selectedStaff.compliance.uan}</strong>
                          </div>
                        )}
                        {selectedStaff.compliance.pfNumber && (
                          <div className="detail-row">
                            <span>PF Number</span>
                            <strong>{selectedStaff.compliance.pfNumber}</strong>
                          </div>
                        )}
                      </>)}
                      {selectedStaff.compliance.esicApplicable && (<>
                        <div className="detail-row">
                          <span>ESIC ({selectedStaff.compliance.esicRate || 0.75}%)</span>
                          <strong>{formatMoney(selectedStaff.payroll?.esicDeduction || 0)}</strong>
                        </div>
                        {selectedStaff.compliance.esicNumber && (
                          <div className="detail-row">
                            <span>ESIC Number</span>
                            <strong>{selectedStaff.compliance.esicNumber}</strong>
                          </div>
                        )}
                      </>)}
                      {selectedStaff.compliance.ptApplicable && (
                        <div className="detail-row">
                          <span>PT ({selectedStaff.compliance.ptState || ""})</span>
                          <strong>{formatMoney(selectedStaff.payroll?.ptDeduction || 0)}</strong>
                        </div>
                      )}
                      {selectedStaff.compliance.tdsApplicable && (
                        <div className="detail-row">
                          <span>TDS {selectedStaff.compliance.tdsAmount ? `(₹${selectedStaff.compliance.tdsAmount}/mo)` : selectedStaff.compliance.tdsRate ? `(${selectedStaff.compliance.tdsRate}%)` : ""}</span>
                          <strong>{formatMoney(selectedStaff.payroll?.tdsDeduction || 0)}</strong>
                        </div>
                      )}
                      {!selectedStaff.compliance.pfApplicable &&
                        !selectedStaff.compliance.esicApplicable &&
                        !selectedStaff.compliance.ptApplicable &&
                        !selectedStaff.compliance.tdsApplicable && (
                          <div className="text-muted small">No compliance configured</div>
                        )}
                    </div>
                  </>
                )}
              </div>

              <div className="punch-log-panel">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <div>
                    <h5 className="mb-0">Punch Logs</h5>
                    <span className="text-muted small">{selectedPunches.length} entries</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Select size="sm" value={logDay} onChange={(e) => setLogDay(e.target.value)} style={{ width: 110 }}>
                      <option value="">All Days</option>
                      {Array.from({ length: daysInMonth }, (_, index) => index + 1).map((item) => (
                        <option key={item} value={item}>{String(item) === getDefaultLogDay(month, year) ? `Today (${item})` : `Day ${item}`}</option>
                      ))}
                    </Form.Select>
                  </div>
                </div>

                <div className="punch-log-list">
                  {selectedPunches.length === 0 ? (
                    <div className="punch-empty-state">No punch records found</div>
                  ) : selectedPunches.map((record) => (
                    <div className="punch-log-item" key={record._id || `${record.dateKey}-${record.punchInAt}`}>
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <strong>{formatDate(record.dateKey)}</strong>
                          <div className="text-muted small">{formatDuration(record.totalMinutes)}</div>
                        </div>
                        <Badge bg={statusVariant[record.status] || "secondary"}>{(record.status || "PRESENT").replace(/_/g, " ")}</Badge>
                      </div>
                      <div className="punch-time-grid">
                        <div><span>In</span><strong>{formatTime(record.punchInAt)}</strong></div>
                        <div><span>Out</span><strong>{formatTime(record.punchOutAt)}</strong></div>
                      </div>
                      {(record.photoIn || record.photoOut) && (
                        <div className="d-flex gap-2 mt-2 align-items-center">
                          {record.photoIn && (
                            <a href={getFileUrl(record.photoIn)} target="_blank" rel="noreferrer" title="Punch In Photo">
                              <img src={getFileUrl(record.photoIn)} alt="In" style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6" }} />
                            </a>
                          )}
                          {record.photoOut && (
                            <a href={getFileUrl(record.photoOut)} target="_blank" rel="noreferrer" title="Punch Out Photo">
                              <img src={getFileUrl(record.photoOut)} alt="Out" style={{ width: 34, height: 34, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6" }} />
                            </a>
                          )}
                        </div>
                      )}
                      <div className="punch-location-box">
                        <div><span>In location</span><strong>{formatLocation(record.locationIn)}</strong></div>
                        <div><span>Out location</span><strong>{formatLocation(record.locationOut)}</strong></div>
                        <div className="d-flex gap-2 flex-wrap" style={{ gridColumn: "1 / -1", background: "transparent", padding: 0 }}>
                          {(record.locationIn?.latitude || record.locationOut?.latitude) && (
                            <button
                              className="punch-map-btn"
                              onClick={() => setMapRecord(record)}
                            >
                              View Map
                            </button>
                          )}
                          <button
                            className="punch-map-btn"
                            style={{ color: "#16a34a", background: "#f0fdf4", borderColor: "#bbf7d0" }}
                            onClick={() => {
                              const uId = selectedStaff?.userId?._id || selectedStaff?.userId || selectedStaff?._id;
                              navigate(`/live-tracking?staffId=${uId}&dateKey=${record.dateKey}`);
                            }}
                          >
                            Live Trail
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map Preview Modal */}
              <MapPreviewModal
                show={Boolean(mapRecord)}
                onHide={() => setMapRecord(null)}
                record={mapRecord}
                onLiveTrack={() => {
                  const uId = selectedStaff?.userId?._id || selectedStaff?.userId || selectedStaff?._id;
                  setMapRecord(null);
                  navigate(`/live-tracking?staffId=${uId}&dateKey=${mapRecord?.dateKey}`);
                }}
              />
            </Col>
          </Row>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedStaff(null)}>Close</Button>
          <Button variant="success" disabled={saving} onClick={saveSelectedAttendance}>{saving ? "Saving..." : "Save Attendance"}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showConfig} onHide={() => setShowConfig(false)} size="lg" enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>Attendance Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Office Start Time</Form.Label>
              <Form.Control
                type="time"
                value={config.officeStartTime || ""}
                onChange={(e) => setConfig({ ...config, officeStartTime: e.target.value })}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Office End Time</Form.Label>
              <Form.Control
                type="time"
                value={config.officeEndTime || ""}
                onChange={(e) => setConfig({ ...config, officeEndTime: e.target.value })}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Grace Minutes</Form.Label>
              <Form.Control
                type="number"
                value={config.graceMinutes}
                onChange={(e) => setConfig({ ...config, graceMinutes: Number(e.target.value) })}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Full Day Minutes</Form.Label>
              <Form.Control
                type="number"
                value={config.fullDayMinutes}
                onChange={(e) => setConfig({ ...config, fullDayMinutes: Number(e.target.value) })}
              />
            </Col>
            <Col md={4}>
              <Form.Label>Half Day Minutes</Form.Label>
              <Form.Control
                type="number"
                value={config.halfDayMinutes}
                onChange={(e) => setConfig({ ...config, halfDayMinutes: Number(e.target.value) })}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Salary Calculation Basis</Form.Label>
              <Form.Select
                value={config.salaryCalculationBasis}
                onChange={(e) => setConfig({ ...config, salaryCalculationBasis: e.target.value })}
              >
                <option value="CALENDAR_DAYS">Calendar days</option>
                <option value="WORKING_DAYS">Working days excluding weekends</option>
                <option value="RECORDED_DAYS">Only recorded attendance days</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Half Day Pay Ratio</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                min="0"
                max="1"
                value={config.halfDayPayRatio}
                onChange={(e) => setConfig({ ...config, halfDayPayRatio: Number(e.target.value) })}
              />
            </Col>
            <Col md={12}>
              <Form.Label>Weekend Days</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                {weekDays.map((day) => (
                  <Form.Check
                    key={day.value}
                    type="checkbox"
                    label={day.label}
                    checked={(config.weekendDays || []).map(Number).includes(day.value)}
                    onChange={() => toggleWeekend(day.value)}
                  />
                ))}
              </div>
            </Col>
            <Col md={12}><hr className="my-1" /></Col>
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div>
                  <Form.Label className="mb-0 fw-bold">Approved Office & Site Locations (Geofencing)</Form.Label>
                  <div className="text-muted small">
                    Employees can punch in/out if they are within the allowed radius of ANY approved location.
                  </div>
                </div>
                <Button size="sm" variant="primary" type="button" onClick={addOfficeLocation}>
                  + Add Office
                </Button>
              </div>

              {(!config.officeLocations || config.officeLocations.length === 0) ? (
                <div className="text-muted small p-3 bg-light rounded text-center border">
                  No offices configured. Click "+ Add Office" to register company offices and project sites.
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {config.officeLocations.map((office, idx) => (
                    <div
                      key={idx}
                      className="p-3 border rounded position-relative"
                      style={{ background: "rgba(127, 127, 127, 0.08)" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="small fw-bold">Office #{idx + 1}</strong>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant={activeMapIndex === idx ? "secondary" : "primary"}
                            className="btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
                            style={{ fontSize: 11, padding: "3px 9px", fontWeight: 600 }}
                            type="button"
                            onClick={() => setActiveMapIndex(activeMapIndex === idx ? null : idx)}
                            title={activeMapIndex === idx ? "Close map" : "Pick location on map"}
                          >
                            {activeMapIndex === idx ? (
                              <>
                                <i className="fas fa-times"></i> Close Map
                              </>
                            ) : (
                              <>
                                <i className="fas fa-map-marker-alt"></i> Set on Map
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="btn-sm d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 11, padding: "3px 9px", fontWeight: 600 }}
                            type="button"
                            disabled={locatingOfficeIndex === idx}
                            onClick={() => handleCaptureLocationForItem(idx)}
                          >
                            {locatingOfficeIndex === idx ? (
                              <>
                                <Spinner animation="border" size="sm" style={{ width: 11, height: 11 }} />
                                <span>Locating...</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-crosshairs"></i>
                                <span>Use My Location</span>
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="btn-sm d-inline-flex align-items-center gap-1"
                            style={{ fontSize: 11, padding: "3px 9px" }}
                            type="button"
                            onClick={() => removeOfficeItem(idx)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>

                      <Row className="g-2">
                        <Col md={4}>
                          <Form.Label className="small mb-1">Office / Site Name</Form.Label>
                          <Form.Control
                            size="sm"
                            value={office.name || ""}
                            onChange={(e) => updateOfficeItem(idx, "name", e.target.value)}
                            placeholder="e.g. Head Office Jaipur"
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Latitude</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="any"
                            value={office.latitude ?? ""}
                            onChange={(e) => updateOfficeItem(idx, "latitude", e.target.value)}
                            placeholder="26.9112"
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Label className="small mb-1">Longitude</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            step="any"
                            value={office.longitude ?? ""}
                            onChange={(e) => updateOfficeItem(idx, "longitude", e.target.value)}
                            placeholder="75.7386"
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small mb-1">Radius (m)</Form.Label>
                          <Form.Control
                            size="sm"
                            type="number"
                            min="10"
                            value={office.allowedRadiusMeters || ""}
                            onChange={(e) => updateOfficeItem(idx, "allowedRadiusMeters", Number(e.target.value || 0))}
                            placeholder="100"
                          />
                        </Col>
                        <Col md={12}>
                          <Form.Label className="small mb-1">Address / Landmark (optional)</Form.Label>
                          <Form.Control
                            size="sm"
                            value={office.address || ""}
                            onChange={(e) => updateOfficeItem(idx, "address", e.target.value)}
                            placeholder="Full street address or landmark"
                          />
                        </Col>
                      </Row>

                      {/* Inline Expandable Google Map Picker */}
                      {activeMapIndex === idx && (
                        <OfficeGoogleMapPicker
                          office={office}
                          onChange={(updates) => {
                            setConfig((current) => {
                              const list = [...(current.officeLocations || [])];
                              list[idx] = { ...list[idx], ...updates };
                              return { ...current, officeLocations: list };
                            });
                          }}
                          onClose={() => setActiveMapIndex(null)}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                label="Paid Weekend"
                checked={config.paidWeekend}
                onChange={(e) => setConfig({ ...config, paidWeekend: e.target.checked })}
              />
              <Form.Check
                type="switch"
                label="Paid Holiday"
                checked={config.paidHoliday}
                onChange={(e) => setConfig({ ...config, paidHoliday: e.target.checked })}
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                label="Auto Half Day From Punch Hours"
                checked={config.autoHalfDayFromPunchHours}
                onChange={(e) => setConfig({ ...config, autoHalfDayFromPunchHours: e.target.checked })}
              />
              <Form.Check
                type="switch"
                label="Mark Absent If Below Half Day"
                checked={config.autoAbsentForShortHours}
                onChange={(e) => setConfig({ ...config, autoAbsentForShortHours: e.target.checked })}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfig(false)}>
            Close
          </Button>
          <Button variant="success" disabled={savingConfig} onClick={saveConfig}>
            {savingConfig ? "Saving..." : "Save Settings"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ASSIGN OFFICE MODAL */}
      <Modal
        show={assignOfficeModal.show}
        onHide={() => setAssignOfficeModal((prev) => ({ ...prev, show: false }))}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
            {assignOfficeModal.staff
              ? `Assign Allowed Punch Office — ${assignOfficeModal.staff.name}`
              : "Bulk Assign Allowed Punch Offices"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!assignOfficeModal.staff && (
            <div className="mb-3">
              <Form.Label className="fw-semibold small">
                Select Staff Members ({assignOfficeModal.selectedStaffIds.length} selected)
              </Form.Label>
              <div className="d-flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ fontSize: 11, padding: "2px 8px" }}
                  onClick={() =>
                    setAssignOfficeModal((prev) => ({
                      ...prev,
                      selectedStaffIds: staff.map((s) => s._id),
                    }))
                  }
                >
                  Select All
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ fontSize: 11, padding: "2px 8px" }}
                  onClick={() =>
                    setAssignOfficeModal((prev) => ({ ...prev, selectedStaffIds: [] }))
                  }
                >
                  Deselect All
                </Button>
              </div>
              <div className="p-2 border rounded bg-light" style={{ maxHeight: 150, overflowY: "auto" }}>
                <Row className="g-2">
                  {staff.map((s) => {
                    const isChecked = assignOfficeModal.selectedStaffIds.includes(s._id);
                    return (
                      <Col md={6} key={s._id}>
                        <Form.Check
                          type="checkbox"
                          id={`staff-sel-${s._id}`}
                          label={s.name}
                          checked={isChecked}
                          onChange={() => {
                            setAssignOfficeModal((prev) => {
                              const exists = prev.selectedStaffIds.includes(s._id);
                              const next = exists
                                ? prev.selectedStaffIds.filter((id) => id !== s._id)
                                : [...prev.selectedStaffIds, s._id];
                              return { ...prev, selectedStaffIds: next };
                            });
                          }}
                        />
                      </Col>
                    );
                  })}
                </Row>
              </div>
            </div>
          )}

          <div className="mb-3">
            <Form.Label className="fw-semibold small">
              Allowed Punch-In / Punch-Out Locations
            </Form.Label>
            <div className="text-muted small mb-3">
              Select which office(s) or site(s) this employee is allowed to punch in from. If no specific office is selected, the employee will be allowed to punch at <strong>All Approved Offices</strong>.
            </div>

            <div className="mb-2">
              <Form.Check
                type="radio"
                name="officeMode"
                id="office-mode-all"
                label={
                  <span>
                    <strong>Any Location (Unrestricted)</strong>
                    <span className="text-muted small d-block">
                      Employee can punch from anywhere without any office geofence restriction.
                    </span>
                  </span>
                }
                checked={assignOfficeModal.selectedOffices.length === 0}
                onChange={handleSelectAllOffices}
              />
            </div>

            <hr className="my-2" />

            <div className="mb-2 fw-semibold small text-dark">
              Or restrict to specific office(s):
            </div>

            {(!config.officeLocations || config.officeLocations.length === 0) ? (
              <div className="text-muted small p-3 bg-light rounded text-center border">
                No offices configured yet. Please configure offices under "Attendance Settings" first.
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {config.officeLocations.map((office, oIdx) => {
                  const offName = office.name || `Office #${oIdx + 1}`;
                  const isChecked = assignOfficeModal.selectedOffices.includes(offName);
                  return (
                    <div
                      key={oIdx}
                      className={`p-2 border rounded d-flex align-items-center justify-content-between ${
                        isChecked ? "bg-light border-primary" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleToggleOffice(offName)}
                    >
                      <Form.Check
                        type="checkbox"
                        id={`off-check-${oIdx}`}
                        label={
                          <div>
                            <strong>{offName}</strong>
                            <div className="text-muted small">
                              Radius: {office.allowedRadiusMeters || 100}m{" "}
                              {office.address ? `| ${office.address}` : ""}
                            </div>
                          </div>
                        }
                        checked={isChecked}
                        onChange={() => {}}
                      />
                      {isChecked && <Badge bg="primary">Allowed</Badge>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setAssignOfficeModal((prev) => ({ ...prev, show: false }))}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={assignOfficeModal.saving}
            onClick={handleSaveAssignedOffices}
          >
            {assignOfficeModal.saving ? "Saving..." : "Save Office Assignment"}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .staff-attendance-detail-modal .modal-content { max-height: calc(100vh - 32px); }
        .staff-attendance-detail-modal .modal-body { display: block; }
        .staff-attendance-detail-modal .table th,
        .staff-attendance-detail-modal .table td { word-break: normal; }
        .calendar-filter-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .calendar-filter-controls .form-select {
          min-width: 120px;
          min-height: 36px;
          border-radius: 8px;
        }
        [data-theme-version="dark"] .calendar-filter-controls .form-select {
          background-color: #111314;
          border-color: #2b2f36;
          color: #ffffff;
        }
        .staff-attendance-side {
          min-height: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .staff-summary-panel,
        .punch-log-panel {
          border: 1px solid #e8edf5;
          border-radius: 12px;
          background: #fff;
          padding: 14px;
        }
        .staff-summary-panel { flex: 0 0 auto; }
        [data-theme-version="dark"] .staff-attendance-detail-modal .modal-content,
        [data-theme-version="dark"] .staff-attendance-detail-modal .modal-header,
        [data-theme-version="dark"] .staff-attendance-detail-modal .modal-body {
          background: #1f1f1f;
          color: #ffffff;
          border-color: #30343b;
        }
        [data-theme-version="dark"] .staff-summary-panel,
        [data-theme-version="dark"] .punch-log-panel {
          background: #1f1f1f;
          border-color: #30343b;
          color: #ffffff;
        }
        [data-theme-version="dark"] .staff-summary-panel .badge.bg-light {
          background-color: #111314 !important;
          border: 1px solid #2b2f36;
          color: #ffffff !important;
        }
        [data-theme-version="dark"] .punch-log-panel .form-select {
          background-color: #111314;
          border-color: #2b2f36;
          color: #ffffff;
        }
        [data-theme-version="dark"] .staff-summary-panel .text-muted,
        [data-theme-version="dark"] .punch-log-panel .text-muted {
          color: #9ca3af !important;
        }
        .punch-log-panel {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
        }
        .salary-metric-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .salary-metric {
          min-width: 0;
          border: 1px solid #edf1f7;
          border-radius: 10px;
          padding: 10px;
          background: #fbfcff;
        }
        .salary-metric-primary {
          background: #f2f7ff;
          border-color: #d9e8ff;
        }
        [data-theme-version="dark"] .salary-metric {
          background: #151719;
          border-color: #2b2f36;
        }
        [data-theme-version="dark"] .salary-metric-primary {
          background: #17202c;
          border-color: #27374a;
        }
        [data-theme-version="dark"] .salary-metric strong {
          color: #ffffff;
        }
        [data-theme-version="dark"] .salary-metric span,
        [data-theme-version="dark"] .salary-metric small {
          color: #9ca3af;
        }
        .salary-metric span,
        .salary-metric small {
          display: block;
          color: #7d86a2;
          font-size: 12px;
          line-height: 1.25;
        }
        .salary-metric strong {
          display: block;
          color: #111827;
          font-size: 18px;
          line-height: 1.2;
          margin: 4px 0;
          word-break: break-word;
        }
        .punch-log-list {
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 2px;
          max-height: 500px;
        }
        .punch-log-item {
          border: 1px solid #edf1f7;
          border-radius: 10px;
          padding: 10px;
          background: #fff;
          margin-bottom: 8px;
        }
        [data-theme-version="dark"] .punch-log-item {
          background: #151719;
          border-color: #2b2f36;
          color: #ffffff;
        }
        .punch-time-grid,
        .punch-location-box {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: 8px;
        }
        .punch-time-grid div,
        .punch-location-box div {
          border-radius: 8px;
          background: #f8fafc;
          padding: 7px 8px;
        }
        .punch-time-grid span,
        .punch-location-box span {
          display: block;
          color: #7d86a2;
          font-size: 11px;
        }
        .punch-time-grid strong,
        .punch-location-box strong {
          color: #111827;
          font-size: 13px;
          word-break: break-word;
        }
        .punch-location-box a {
          grid-column: 1 / -1;
          font-size: 12px;
        }
        .punch-map-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-top: 4px;
          padding: 4px 12px;
          font-size: 12px;
          font-weight: 600;
          color: #2563eb;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s, border-color 0.15s;
          width: fit-content;
        }
        .punch-map-btn:hover {
          background: #dbeafe;
          border-color: #93c5fd;
        }
        [data-theme-version="dark"] .punch-map-btn {
          color: #93c5fd;
          background: #1e2d40;
          border-color: #2563eb;
        }
        [data-theme-version="dark"] .punch-map-btn:hover {
          background: #1d3a5f;
        }
        [data-theme-version="dark"] .punch-time-grid div,
        [data-theme-version="dark"] .punch-location-box div {
          background: #111314;
        }
        [data-theme-version="dark"] .punch-time-grid strong,
        [data-theme-version="dark"] .punch-location-box strong {
          color: #ffffff;
        }
        .punch-empty-state {
          border: 1px dashed #d8dfeb;
          border-radius: 10px;
          color: #7d86a2;
          padding: 24px 12px;
          text-align: center;
        }
        [data-theme-version="dark"] .punch-empty-state {
          border-color: #30343b;
          color: #9ca3af;
        }
        .detail-section-title {
          font-size: 12px;
          font-weight: 700;
          text-transform: uppercase;
          color: #7d86a2;
          letter-spacing: 0.5px;
          border-top: 1px solid #edf1f7;
          padding-top: 10px;
        }
        [data-theme-version="dark"] .detail-section-title {
          color: #9ca3af;
          border-color: #2b2f36;
        }
        .detail-grid {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 5px 8px;
          border-radius: 6px;
          background: #f8fafc;
          font-size: 13px;
        }
        .detail-row-highlight {
          background: #eef6ff;
          font-weight: 600;
        }
        .detail-row span { color: #6b7280; }
        .detail-row strong { color: #111827; font-size: 13px; }
        [data-theme-version="dark"] .detail-row {
          background: #151719;
        }
        [data-theme-version="dark"] .detail-row-highlight {
          background: #17202c;
        }
        [data-theme-version="dark"] .detail-row span { color: #9ca3af; }
        [data-theme-version="dark"] .detail-row strong { color: #ffffff; }
        @media (max-width: 991.98px) {
          .staff-attendance-side { overflow: visible; }
          .punch-log-list { max-height: 320px; }
        }
        .staff-attendance-detail-modal .modal-content { max-height: calc(100vh - 32px); overflow: hidden; }
        .staff-attendance-detail-modal .modal-body { overflow-y: auto !important; }
      `}</style>
    </Fragment>
  );
};

export default StaffAttendancePage;
